/**
 * ManualOverview Component
 *
 * Overview page shown when no specific section is selected.
 * Displays a welcome message and top-level sections for quick navigation.
 * Story 1-5: ManualPage Layout Integration
 */

import type { TreeNode } from '../../lib/manualApi';

interface ManualOverviewProps {
  tree: TreeNode[];
  onNavigate: (slug: string) => void;
}

/**
 * ManualOverview - Welcome/landing view for the manual
 *
 * Shows when user visits /handleiding without a specific slug.
 * Provides quick access to main sections (parts) of the manual.
 */
export function ManualOverview({ tree, onNavigate }: ManualOverviewProps) {
  // Get top-level sections for quick navigation
  // Prefer 'part' sections if available, otherwise show all root-level items
  const partSections = tree.filter((node) => node.sectionType === 'part');
  const topLevelSections = partSections.length > 0 ? partSections : tree;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-8" data-testid="manual-content">
      {/* Welcome header */}
      <div className="text-center mb-8">
        <div className="text-6xl mb-4">📚</div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Welkom bij de Handleiding
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Ontdek alles over het Functieloonhuis beoordelingssysteem.
          Selecteer een onderwerp in de navigatie of kies hieronder een hoofdstuk.
        </p>
      </div>

      {/* Quick navigation to main sections */}
      {topLevelSections.length > 0 && (
        <div className="mt-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 text-center">
            Hoofdstukken
          </h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {topLevelSections.map((section) => (
              <button
                key={section.id}
                type="button"
                onClick={() => onNavigate(section.slug)}
                className="flex flex-col items-start p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-blue-50 hover:border-blue-300 transition-colors text-left group"
              >
                <span className="text-lg font-medium text-gray-900 group-hover:text-blue-600">
                  {section.title}
                </span>
                {section.children.length > 0 && (
                  <span className="text-sm text-gray-500 mt-1">
                    {section.children.length} {section.children.length === 1 ? 'sectie' : 'secties'}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Empty state when no sections */}
      {tree.length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-500">
            Er zijn momenteel geen handleiding secties beschikbaar voor uw rol.
          </p>
        </div>
      )}
    </div>
  );
}
